/**
 * A logic grid, drawn as ornament.
 *
 * The press publishes deduction puzzles, and a partly-solved grid is the most
 * recognisable object in that world — ruled boxes, a few ticks, a scatter of
 * crosses. It also looks like a printed form from a hundred years ago, which is
 * the register the rest of the design is in, so it earns its place twice.
 *
 * The staircase is the real one. With three categories the first category's
 * values run down the side against both of the others, and the last category
 * steps in underneath against only the first — so the bottom-right block does
 * not exist. `hasBlock` in src/logic-grid/pdf/grid.ts decides the same shape
 * for print; drawing a full square here would be a picture of a grid that the
 * press does not actually publish.
 *
 * The marks are consistent, not decorative noise: no row or column carries two
 * ticks, and no cross sits where a tick has already claimed the row or column.
 * People who solve these for a living will look, and a nonsense grid on the
 * front page of a press that promises checked puzzles would be a bad joke.
 */

const CELL = 22;
/** Values per category, so one block is BLOCK x BLOCK cells. */
const BLOCK = 4;
/** Room down the left and across the top for the label strokes. */
const GUTTER = 58;

/** Confirmed pairs — at most one per row and per column of any block. */
const TICKS: Array<[number, number]> = [
  [0, 2],
  [1, 0],
  [2, 7],
  [5, 1],
];

/** Eliminations. None contradicts a tick. */
const CROSSES: Array<[number, number]> = [
  [0, 0],
  [0, 1],
  [1, 3],
  [2, 1],
  [3, 0],
  [3, 2],
  [1, 5],
  [3, 7],
  [4, 1],
  [5, 3],
  [6, 0],
  [7, 3],
];

const x = (col: number) => GUTTER + col * CELL;
const y = (row: number) => GUTTER + row * CELL;

const SIZE = GUTTER + BLOCK * 2 * CELL;
/** Where the second block begins on both axes — and where the stair steps. */
const MID = GUTTER + BLOCK * CELL;

/** Short strokes standing in for the category labels. */
function labelStrokes(vertical: boolean) {
  return Array.from({ length: BLOCK * 2 }, (_, i) => {
    const at = (vertical ? x(i) : y(i)) + CELL / 2;
    const len = 14 + ((i * 7) % 18);
    return vertical ? (
      <line key={i} x1={at} y1={GUTTER - 8} x2={at} y2={GUTTER - 8 - len} />
    ) : (
      <line key={i} x1={GUTTER - 8} y1={at} x2={GUTTER - 8 - len} y2={at} />
    );
  });
}

export default function GridEmblem({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      viewBox={`0 0 ${SIZE} ${SIZE}`}
      role="img"
      aria-label="A partly solved logic grid"
      xmlns="http://www.w3.org/2000/svg"
    >
      <g stroke="var(--line)" strokeWidth={3} strokeLinecap="round">
        {labelStrokes(true)}
        {labelStrokes(false)}
      </g>

      {/* Cell rules. Past the step, a rule stops at MID rather than running on
          into the block that isn't there. */}
      <g stroke="var(--line)" strokeWidth={1}>
        {Array.from({ length: BLOCK * 2 + 1 }, (_, i) => (
          <line key={`h${i}`} x1={GUTTER} y1={y(i)} x2={i <= BLOCK ? SIZE : MID} y2={y(i)} />
        ))}
        {Array.from({ length: BLOCK * 2 + 1 }, (_, i) => (
          <line key={`v${i}`} x1={x(i)} y1={GUTTER} x2={x(i)} y2={i <= BLOCK ? SIZE : MID} />
        ))}
      </g>

      {/* The staircase outline and the two block divisions, heavier — the way
          printed grids separate one category from the next. */}
      <g stroke="var(--ink)" strokeWidth={2} fill="none" opacity={0.55}>
        <path
          d={`M${GUTTER} ${GUTTER} H${SIZE} V${MID} H${MID} V${SIZE} H${GUTTER} Z`}
          strokeLinejoin="round"
        />
        <line x1={MID} y1={GUTTER} x2={MID} y2={SIZE} />
        <line x1={GUTTER} y1={MID} x2={SIZE} y2={MID} />
      </g>

      <g stroke="var(--ink)" strokeWidth={2} strokeLinecap="round" opacity={0.45}>
        {CROSSES.map(([r, c]) => (
          <g key={`x${r}-${c}`}>
            <line x1={x(c) + 6} y1={y(r) + 6} x2={x(c + 1) - 6} y2={y(r + 1) - 6} />
            <line x1={x(c + 1) - 6} y1={y(r) + 6} x2={x(c) + 6} y2={y(r + 1) - 6} />
          </g>
        ))}
      </g>

      {/* The only rust in the emblem, so the eye lands on what has been
          established rather than on what has been ruled out. */}
      <g stroke="var(--rust)" strokeWidth={3} strokeLinecap="round" strokeLinejoin="round">
        {TICKS.map(([r, c]) => (
          <polyline
            key={`t${r}-${c}`}
            fill="none"
            points={`${x(c) + 5},${y(r) + 11} ${x(c) + 9},${y(r) + 16} ${x(c + 1) - 5},${y(r) + 6}`}
          />
        ))}
      </g>
    </svg>
  );
}
